import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/components/Toast';
import type { SignupSheet } from '@flock-types/signupSheets.types';
import type { SheetUpdates } from '@/services/signupSheets.service';

interface Props {
  sheet: SignupSheet;
  updateSheet: (updates: SheetUpdates) => Promise<void>;
  isMutating: boolean;
  onClose: () => void;
}

/** Edit sheet title/description (port of the Expo edit-sheet modal). */
export default function EditSheetModal({ sheet, updateSheet, isMutating, onClose }: Props) {
  const { showToast } = useToast();
  const [title, setTitle] = useState(sheet.title);
  const [description, setDescription] = useState(sheet.description || '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!title.trim()) return;
    try {
      await updateSheet({
        title: title.trim(),
        description: description.trim() || undefined,
      });
      onClose();
    } catch {
      showToast('Failed to update sheet.', 'error');
    }
  };

  const inputClass =
    'w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">Edit Sign-Up Sheet</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Title *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Instructions or details"
              className={inputClass}
            />
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <button
            onClick={onClose}
            className="min-h-touch flex-1 rounded-lg bg-flock-gray-200 px-4 py-2.5 text-sm font-medium text-flock-navy-700 transition hover:bg-flock-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isMutating || !title.trim()}
            className="min-h-touch flex-1 rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
          >
            {isMutating ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
